import {
	StylovyzeDialog,
	decoratedAttributes,
	useGlobalization,
	Text,
} from '@innovyze/stylovyze';
import {
	Card,
	CardActionArea,
	CardContent,
	type DialogProps,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	type SelectChangeEvent,
	Stack,
	TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
	selectRehabTreesIsCreating,
	selectRehabTreesList,
	selectRehabTreesNameError,
} from '@Selectors';
import {
	addRehabTree,
	setNewTreeName,
	setRehabTreeNameError,
} from '@Actions/index';
import { Custom } from '@Components/Utils/icons';
import { AGSetMode, TreeType } from '@Types';
import { AutoFixNormal } from '@mui/icons-material';
import {
	useEnabledAssetTypes,
	useEnabledInspectionStandards,
	useEnabledSystemTypes,
} from './useEnabledAssetTypes';
import {
	type AssetTypeSummary,
	getNewAssetIds,
	type SystemType,
} from '@innovyze/lib_asset_schema';
import { useAssetSchema } from '../../hooks/assetSchemaHooks';

interface Props {
	open: boolean;
	onClose: () => void;
	onCreateAutomatic: () => void;
}

export const NewAGSet = ({ open, onClose, onCreateAutomatic }: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();

	const existingTrees = selectRehabTreesList();

	const [newName, setNewName] = useState('');
	const [newDescription, setNewDesciption] = useState('');
	const [newType, setNewType] = useState<AssetTypeSummary | undefined>(
		undefined,
	);
	const [newSystemType, setNewSystemType] = useState<SystemType | undefined>(
		undefined,
	);
	const [inspectionStandard, setInspectionStandard] = useState<
		string | undefined
	>(undefined);
	const [selectedMode, setSelectedMode] = useState(AGSetMode.AUTOMATIC);

	const { enabledSystemTypes, status: pickerStatus } =
		useEnabledSystemTypes();
	const { enabledAssetTypes } = useEnabledAssetTypes(newSystemType);
	const { data: newAssetSchema, status: schemaStatus } = useAssetSchema(
		newSystemType?.id ?? '',
		newType?.id ?? '',
	);
	const { enabledInspectionStandards } =
		useEnabledInspectionStandards(newAssetSchema);

	const nameError = selectRehabTreesNameError();
	const isCreating = selectRehabTreesIsCreating();

	const handleCancelNewSet = () => {
		onClose();
	};

	useEffect(() => {
		setNewName('');
		dispatch(setRehabTreeNameError(t('Name is required')));
		setNewDesciption('');
		setNewSystemType(undefined);
		setNewType(undefined);
		setInspectionStandard(undefined);
		setSelectedMode(AGSetMode.AUTOMATIC);
	}, [open]);

	const handleCreateNewSet = () => {
		if (
			nameError.length === 0 &&
			newSystemType &&
			newType &&
			newAssetSchema &&
			(enabledInspectionStandards.length === 0 || inspectionStandard)
		) {
			if (newName.trim().length === 0) {
				dispatch(setRehabTreeNameError(t('Name is required')));
				return;
			}

			const { systemTypeId, assetTypeId } = getNewAssetIds({
				assetSchema: newAssetSchema,
			});

			dispatch(
				setNewTreeName({
					name: newName.trim(),
					description: newDescription.trim(),
					systemType: systemTypeId,
					assetType: assetTypeId,
					treeNodes: [],
					inspectionStandard: inspectionStandard,
					mode: selectedMode,
					treeType: TreeType.AGSet,
				}),
			);

			// Custom create the tree now, Automatic does it after the next phase.
			if (selectedMode == AGSetMode.CUSTOM)
				dispatch(
					addRehabTree({
						history: history,
						runTree: false,
						historyReplace: false,
					}),
				);
			else onCreateAutomatic();
		}
	};

	const handlelNameChangeValue = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		let error = '';
		if (event.target && event.target.value !== '') {
			setNewName(event.target.value);
			const duplicateName = existingTrees.filter(
				tree =>
					tree.name.toUpperCase().trim() ===
					event.target.value.toUpperCase().trim(),
			);

			if (duplicateName.length > 0) {
				error = t('Enter a unique name');
			} else {
				const rexp = new RegExp('^[a-zA-Z0-9 ]+$');
				const test = rexp.test(event.target.value);
				if (!test) {
					error = t('Only alphanumeric characters are allowed');
				} else {
					const rexp2 = new RegExp('^.{1,50}$');
					const test2 = rexp2.test(event.target.value);
					if (!test2) {
						error = t(
							'Name cannot contain more than 50 characters',
						);
					}
				}
			}
		} else {
			setNewName('');
			error = t('Name is required');
		}
		dispatch(setRehabTreeNameError(error));
	};

	const handleDecriptionChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const target = event?.target;
		if (target != undefined) {
			setNewDesciption(target.value);
		}
	};

	const handleSystemTypeSelectChange = (event: SelectChangeEvent<string>) => {
		const selectedType = event?.target.value;

		if (selectedType === '') {
			setNewSystemType(undefined);
		} else {
			const systemType = enabledSystemTypes.find(
				type => type.id === selectedType,
			);
			setNewSystemType(systemType);
		}

		setNewType(undefined);
		setInspectionStandard(undefined);
	};

	const handleAssetTypeSelectChange = (event: SelectChangeEvent<string>) => {
		const selectedType = event?.target.value;
		if (selectedType === '') {
			setNewType(undefined);
		} else {
			const assetType = enabledAssetTypes.find(
				type => type.id === selectedType,
			);
			setNewType(assetType);
			// Every enabled asset type is Rehab capable, so supports automatic setup.
			setSelectedMode(AGSetMode.AUTOMATIC);
		}
		setInspectionStandard(undefined);
	};

	const handleInspectionStandardSelectChange = (
		event: SelectChangeEvent<string>,
	) => {
		const selectedStandard = event?.target.value;

		setInspectionStandard(selectedStandard);
	};

	// This stops the dialog closing when clicked on the background
	const onCloseDialog = () => void {};

	const dialogProps: DialogProps = {
		open: open,
		maxWidth: 'md',
		onClose: onCloseDialog,
	};

	// The pickers are empty while the catalogue loads and when it fails or the
	// release flag is closed, so say which it is instead of showing blank lists.
	const pickerMessage = (() => {
		if (pickerStatus === 'loading') {
			return t('Loading system types');
		}
		if (pickerStatus === 'error') {
			return t('Unable to load system types. Try again later.');
		}
		if (pickerStatus === 'empty' || pickerStatus === 'noCapable') {
			return t('No system types are available');
		}
		return undefined;
	})();
	const schemaMessage = (() => {
		if (!newSystemType || !newType) {
			return undefined;
		}
		if (schemaStatus === 'error') {
			return t('Unable to load this asset type. Try again later.');
		}
		if (schemaStatus === 'pending') {
			return t('Loading asset type');
		}
		return undefined;
	})();

	const isCreateButtonDisabled = () =>
		isCreating ||
		nameError.length !== 0 ||
		!newSystemType ||
		!newType ||
		!newAssetSchema ||
		(enabledInspectionStandards.length !== 0 && !inspectionStandard);

	const adpAttrs = decoratedAttributes({
		systemtype: newSystemType?.displayName ?? '',
		inspectionstandard: inspectionStandard ?? '',
		name: newName,
		type: newType?.displayName ?? '',
		disablestandardtypes: (
			enabledInspectionStandards.length === 0
		).toString(),
	});

	return (
		<StylovyzeDialog
			dialogProps={dialogProps}
			data-cy="new-dialog"
			title={t('New Asset Group Set')}
			open={open}
			cancelText={t('Cancel')}
			cancelButtonProps={{
				disabled: isCreating,
				dataCy: 'create-cancel',
			}}
			onCancel={handleCancelNewSet}
			confirmText={t('Create Set')}
			confirmButtonProps={{
				disabled: isCreateButtonDisabled(),
				dataCy: 'create-confirm',
				'adp-click': 'true',
				id: 'create-ags',
				type: 'button',
				name: 'Create Set',
				...adpAttrs,
			}}
			onConfirm={handleCreateNewSet}>
			<Stack style={{ width: '600px' }}>
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						error={nameError.length !== 0}
						required
						name="name"
						label={t('Enter Name')}
						variant="outlined"
						onChange={handlelNameChangeValue}
						helperText={nameError.length !== 0 ? nameError : ' '}
					/>
				</FormControl>
				<FormControl variant="outlined" margin="normal" fullWidth>
					<TextField
						fullWidth
						name="description"
						label={t('Add Description')}
						variant="outlined"
						onChange={handleDecriptionChange}
						value={newDescription}
					/>
					<FormHelperText> </FormHelperText>
				</FormControl>
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel
						id="system-type-select-label"
						variant="outlined"
						error={!newSystemType}>
						{t('Select System Type')}
					</InputLabel>
					<Select
						labelId="system-type-select-label"
						data-cy="systemTypeField"
						name="systemType"
						error={!newSystemType}
						onChange={handleSystemTypeSelectChange}
						label={t('Select System Type')}
						value={newSystemType?.id}>
						{enabledSystemTypes.map((systemType, index) => {
							return (
								<MenuItem value={systemType.id} key={index}>
									{t(systemType.displayName)}
								</MenuItem>
							);
						})}
					</Select>
					<FormHelperText error>
						{pickerMessage ??
							(!newSystemType ? t('Select a System Type') : ' ')}
					</FormHelperText>
				</FormControl>
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel
						variant="outlined"
						error={newSystemType && !newType}>
						{t('Select Asset Type')}
					</InputLabel>
					<Select
						data-cy="assetTypeField"
						name="assetType"
						value={newType?.id}
						error={newSystemType && !newType}
						onChange={handleAssetTypeSelectChange}
						label={t('Select Asset Type')}>
						{enabledAssetTypes.map((assetType, index) => {
							return (
								<MenuItem value={assetType.id} key={index}>
									{t(assetType.displayName)}
								</MenuItem>
							);
						})}
					</Select>
					<FormHelperText error>
						{schemaMessage ??
							(newSystemType && !newType
								? t('Select an Asset Type')
								: ' ')}
					</FormHelperText>
				</FormControl>
				<FormControl variant="outlined" margin="normal" fullWidth>
					<InputLabel
						id="inspection-standard-select-label"
						variant="outlined"
						error={
							enabledInspectionStandards.length !== 0 &&
							!inspectionStandard
						}>
						{t('Select Inspection Standard')}
					</InputLabel>
					<Select
						data-cy="inspectionStandardField"
						disabled={enabledInspectionStandards.length === 0}
						name="inspectionStandard"
						error={
							enabledInspectionStandards.length !== 0 &&
							!inspectionStandard
						}
						onChange={handleInspectionStandardSelectChange}
						label={t('Select Inspection Standard')}
						value={inspectionStandard}>
						{enabledInspectionStandards.map(
							(inspectionStandard, index) => {
								return (
									<MenuItem
										value={inspectionStandard}
										key={index}>
										{t(inspectionStandard)}
									</MenuItem>
								);
							},
						)}
					</Select>
					<FormHelperText error>
						{enabledInspectionStandards.length !== 0 &&
						!inspectionStandard
							? t('Select an Inspection Standard')
							: ' '}
					</FormHelperText>
				</FormControl>
				<Grid container spacing={2} style={{ paddingTop: '1rem' }}>
					<Grid item container xs={12}>
						<Text variant="heading-medium-strong">
							{t(
								'How would you like to set up your asset group set?',
							)}
						</Text>
					</Grid>
					<Grid item container xs={12}>
						<Text variant="text-medium">
							{t('Choose which setup method you want to use.')}
						</Text>
					</Grid>
					<Grid item xs={6}>
						<Card style={{ height: '100%' }}>
							<CardActionArea
								disabled={!newType}
								onClick={() =>
									setSelectedMode(AGSetMode.AUTOMATIC)
								}
								data-active={
									selectedMode === AGSetMode.AUTOMATIC
										? ''
										: undefined
								}
								sx={{
									height: '100%',
									'&[data-active]': {
										backgroundColor: 'action.selected',
										'&:hover': {
											backgroundColor:
												'action.selectedHover',
										},
									},
								}}>
								<CardContent sx={{ height: '100%' }}>
									<Grid container spacing={2}>
										<Grid
											item
											xs={1.5}
											alignContent={'center'}>
											<AutoFixNormal />
										</Grid>
										<Grid item xs={10.5}>
											<Text variant="heading-medium-strong">
												{t('Automatic setup')}
											</Text>
										</Grid>
										<Grid item xs={12}>
											<Text variant="text-medium">
												{t(
													'Automatic setup based on asset attributes: material, diameter, and year. You can still refine groups later.',
												)}
											</Text>
										</Grid>
									</Grid>
								</CardContent>
							</CardActionArea>
						</Card>
					</Grid>
					<Grid item xs={6}>
						<Card style={{ height: '100%' }}>
							<CardActionArea
								onClick={() =>
									setSelectedMode(AGSetMode.CUSTOM)
								}
								data-active={
									selectedMode === AGSetMode.CUSTOM
										? ''
										: undefined
								}
								sx={{
									height: '100%',
									'&[data-active]': {
										backgroundColor: 'action.selected',
										'&:hover': {
											backgroundColor:
												'action.selectedHover',
										},
									},
								}}>
								<CardContent sx={{ height: '100%' }}>
									<Grid container spacing={2}>
										<Grid
											item
											xs={1.5}
											alignContent={'center'}>
											<Custom />
										</Grid>
										<Grid item xs={10.5}>
											<Text variant="heading-medium-strong">
												{t('Custom setup')}
											</Text>
										</Grid>
										<Grid item xs={12}>
											<Text variant="text-medium">
												{t(
													'Create groups manually using available attributes or data types.',
												)}
											</Text>
										</Grid>
									</Grid>
								</CardContent>
							</CardActionArea>
						</Card>
					</Grid>
				</Grid>
			</Stack>
		</StylovyzeDialog>
	);
};
